import { useState, useCallback } from "react";

import { css } from "@compiled/react";

import { Picture } from "@/components/wpl/image/src";
import { useBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client";
import { Lozenge } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";

// Constants
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FORM_CONFIG = {
    locale: "US-CA",
    formId: "a9ce9de2-9120-4fa9-a982-12a369254cad",
    site: "atlassian"
} as const;

const CONTENT = {
    lastUpdated: "December 18th, 2025",
    heading: "Cloud Roadmap",
    description:
        "Check out the latest features in Cloud and learn about what we're working on. Subscribe to receive an email each quarter highlighting our newly released features, updated time frames, and more.",
    emailLabel: "Your company email",
    submitButton: "Subscribe",
    successMessage: "Thanks for subscribing!",
    errors: {
        invalidEmail: "Enter a valid email address.",
        serverError: "There was a server error submitting. Please try again."
    }
} as const;

const IMAGES = {
    roadmap:
        "https://dam-cdn.atl.orangelogic.com/AssetLink/5i7x2g3g1ap10qp6r31tyi2tw4ho75dq.png",
    checkmark:
        "https://dam-cdn.atl.orangelogic.com/AssetLink/checkmark-success-icon.svg"
} as const;

// Helper function
const isValidEmail = (email: string): boolean => {
    return EMAIL_REGEX.test(email);
};

export const RoadmapSubscriptionForm = () => {
    const [email, setEmail] = useState("");
    const [honeypot, setHoneypot] = useState("");
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const bxpClient = useBxpWebStargateClient();

    const handleSubmit = useCallback(
        async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();

            // Honeypot check - silent return if bot
            if (honeypot) {
                return;
            }

            // Validate email
            if (!email || !isValidEmail(email)) {
                setError(CONTENT.errors.invalidEmail);
                return;
            }

            setIsLoading(true);
            setError("");

            try {
                if (!bxpClient?.updateConsentPreferences) {
                    throw new Error("Consent service unavailable");
                }

                await bxpClient.updateConsentPreferences({
                    locale: FORM_CONFIG.locale,
                    formId: FORM_CONFIG.formId,
                    site: FORM_CONFIG.site,
                    email,
                    formUrl: window.location.href,
                    consentLabelText: "",
                    privacyPolicyText: "",
                    consentCheckboxValue: true
                });

                setIsSubmitted(true);
            } catch (err) {
                const errorMessage =
                    err instanceof Error
                        ? err.message
                        : CONTENT.errors.serverError;
                setError(errorMessage);
            } finally {
                setIsLoading(false);
            }
        },
        [email, honeypot, bxpClient]
    );

    return (
        <div css={parentContainerStyle}>
            <div css={containerStyle}>
                <div css={rowStyle}>
                    <div css={rightColumnStyle}>
                        <div css={imageContainerStyle}>
                            <Picture
                                image={{
                                    src: IMAGES.roadmap,
                                    alt: "Cloud Roadmap"
                                }}
                                style={{
                                    width: "100%",
                                    height: "auto",
                                    maxHeight: "100%"
                                }}
                            />
                        </div>
                    </div>

                    <div css={leftColumnStyle}>
                        <div css={lastUpdatedContainerStyle}>
                            <Lozenge text="Last Updated" appearance="purple" />
                            <p css={dateTextStyle}>
                                &nbsp; {CONTENT.lastUpdated}
                            </p>
                        </div>

                        <h1 css={headingStyle}>{CONTENT.heading}</h1>

                        <p css={descriptionStyle}>{CONTENT.description}</p>

                        {!isSubmitted ? (
                            <form
                                css={formStyle}
                                onSubmit={handleSubmit}
                                noValidate
                                aria-label="Roadmap subscription form"
                            >
                                <label
                                    htmlFor="email-subscription"
                                    css={labelStyle}
                                >
                                    {CONTENT.emailLabel}
                                </label>
                                <div css={emailInputContainerStyle}>
                                    <input
                                        type="email"
                                        name="email"
                                        id="email-subscription"
                                        value={email}
                                        onChange={(e) =>
                                            setEmail(e.target.value)
                                        }
                                        required
                                        disabled={isLoading}
                                        aria-invalid={!!error}
                                        aria-describedby={
                                            error ? "email-error" : undefined
                                        }
                                        css={emailInputStyle}
                                    />
                                    <input
                                        type="submit"
                                        value={
                                            isLoading
                                                ? "Subscribing..."
                                                : CONTENT.submitButton
                                        }
                                        disabled={isLoading}
                                        css={submitButtonStyle}
                                        data-event="clicked"
                                        data-event-component="linkButton"
                                        data-event-container="exactTargetForm"
                                    />
                                </div>
                                {/* Honeypot field */}
                                <div css={honeypotStyle}>
                                    <input
                                        type="text"
                                        name="list_name"
                                        aria-label="Keep this field blank"
                                        value={honeypot}
                                        onChange={(e) =>
                                            setHoneypot(e.target.value)
                                        }
                                        tabIndex={-1}
                                    />
                                </div>
                                {error && (
                                    <div
                                        css={errorStyle}
                                        id="email-error"
                                        role="alert"
                                    >
                                        {error}
                                    </div>
                                )}
                            </form>
                        ) : (
                            <div
                                css={successMessageContainerStyle}
                                role="status"
                                aria-live="polite"
                            >
                                <Picture
                                    image={{
                                        src: IMAGES.checkmark,
                                        alt: "Success",
                                        width: 32,
                                        height: 32
                                    }}
                                />
                                <p css={successMessageTextStyle}>
                                    {CONTENT.successMessage}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const parentContainerStyle = css({
    boxShadow: `0 10px 14px ${wacToken("color.neutral.light100")}`,
    marginBlockEnd: "20px"
});

const containerStyle = css({
    maxWidth: 1200,
    position: "relative",
    marginInlineStart: "auto",
    marginInlineEnd: "auto"
});

const rowStyle = css({
    display: "flex",
    flexWrap: "wrap",
    gap: "40px",
    alignItems: "flex-start"
});

const leftColumnStyle = css({
    "flex": "1 1 100%",
    "minWidth": 0,
    "maxWidth": "600px",
    "display": "flex",
    "flexDirection": "column",
    "gap": "20px",
    "order": 2,
    "padding": "16px",
    "@media (min-width: 768px)": {
        order: 1,
        flex: "1 1 50%"
    }
});

const rightColumnStyle = css({
    "flex": "1 1 100%",
    "minWidth": 0,
    "display": "flex",
    "justifyContent": "center",
    "alignItems": "stretch",
    "order": 1,
    "@media (min-width: 768px)": {
        order: 2,
        flex: "1 1 40%",
        alignSelf: "stretch"
    }
});

const lastUpdatedContainerStyle = css({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap"
});

const dateTextStyle = css({
    margin: 0,
    fontSize: "14px"
});

const headingStyle = css({
    fontSize: "48px",
    fontWeight: 700,
    margin: 0,
    lineHeight: 1.2
});

const descriptionStyle = css({
    fontSize: "16px",
    lineHeight: 1.6,
    margin: 0
});

const formStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    maxWidth: "500px"
});

const labelStyle = css({
    fontSize: "14px",
    fontWeight: 600,
    display: "block"
});

const emailInputContainerStyle = css({
    display: "flex",
    gap: "12px",
    alignItems: "stretch"
});

const emailInputStyle = css({
    "width": "100%",
    "padding": "10px 12px",
    "fontSize": "14px",
    "border": `1px solid ${wacToken("color.neutral.dark100")}`,
    "borderRadius": "4px",
    "outline": "none",
    ":focus": {
        borderColor: wacToken("color.primary.blue"),
        boxShadow: `0 0 0 2px ${wacToken("color.primary.blue")}33`
    }
});

const honeypotStyle = css({
    position: "absolute",
    insetInlineStart: "-9999px",
    width: "1px",
    height: "1px",
    overflow: "hidden"
});

const submitButtonStyle = css({
    "padding": "10px 24px",
    "fontSize": "16px",
    "fontWeight": 600,
    "color": wacToken("color.neutral.light0"),
    "backgroundColor": wacToken("color.primary.blue"),
    "border": "none",
    "borderRadius": "4px",
    "cursor": "pointer",
    "transition": "background-color 0.2s",
    "whiteSpace": "nowrap",
    ":hover:not(:disabled)": {
        backgroundColor: wacToken("color.primary.blue")
    },
    ":active:not(:disabled)": {
        transform: "translateY(1px)"
    },
    ":disabled": {
        opacity: 0.6,
        cursor: "not-allowed"
    }
});

const errorStyle = css({
    color: wacToken("color.secondary.red"),
    fontSize: "14px",
    marginBlockStart: "8px"
});

const successMessageContainerStyle = css({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "16px",
    backgroundColor: wacToken("color.secondary.green"),
    borderRadius: "4px"
});

const successMessageTextStyle = css({
    margin: 0,
    fontSize: "16px"
});

const imageContainerStyle = css({
    marginBlockEnd: "-14px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    height: "100%"
});
